import ConfigAPI from '@/config/api'
import Session from '@/handler/session'

const DB_NAME = 'zyncro-printer'
const DB_VERSION = 1
const STORE = 'local-ticket-config'

// ── IndexedDB ─────────────────────────────────────────────────────────────────

function openDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION)
    req.onupgradeneeded = e => e.target.result.createObjectStore(STORE)
    req.onsuccess = e => resolve(e.target.result)
    req.onerror = e => reject(e.target.error)
  })
}

async function idbGet(key) {
  const db = await openDB()
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readonly')
    const req = tx.objectStore(STORE).get(key)
    req.onsuccess = e => resolve(e.target.result)
    req.onerror = e => reject(e.target.error)
  })
}

async function idbSet(key, value) {
  const db = await openDB()
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite')
    const req = tx.objectStore(STORE).put(value, key)
    req.onsuccess = () => resolve()
    req.onerror = e => reject(e.target.error)
  })
}

// ── Cache warming ─────────────────────────────────────────────────────────────

async function warmCache(reference) {
  try {
    const url = ConfigAPI.baseURL + 'reports/local-ticket-cache' + Session.getToken() + '&reference=' + encodeURIComponent(reference)
    const res = await fetch(url)
    if (!res.ok) return
    const payload = await res.json()
    await idbSet(reference, { ...payload, cachedAt: Date.now() })
  } catch (_) {
    // sin conexión — se usa el cache existente
  }
}

// ── Renderer JS (port de ReportsController::renderReports + renderEscposCommands) ──

function toSnakeCase(str) {
  return str.charAt(0).toLowerCase() + str.slice(1).replace(/[A-Z]+/g, m => '_' + m.toLowerCase())
}

function resolvePath(obj, path) {
  const keys = path.trim().split('.')
  let val = obj
  for (const key of keys) {
    if (val == null) return undefined
    val = val[key] !== undefined ? val[key] : val[toSnakeCase(key)]
  }
  return val
}

function applyFormat(value, fmt) {
  if (value === undefined || value === null) return ''
  if (!fmt) return String(value)
  if (fmt === 'date') {
    const d = new Date(value)
    if (isNaN(d)) return String(value)
    return d.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' })
  }
  if (fmt === 'currency') {
    return '$ ' + Number(value).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  }
  if (fmt === 'decimal') {
    return Number(value).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  }
  if (fmt === 'int') return Math.round(Number(value)).toLocaleString('es-AR')
  if (fmt === 'pointSales') return String(value).padStart(4, '0')
  if (fmt === 'numberInvoice') return String(value).padStart(8, '0')
  if (fmt === 'si-no') return value ? 'SI' : 'NO'
  return String(value)
}

function substituteVars(content, data) {
  return content.replace(/{{([^}]+)}}/g, (match, expr) => {
    if (expr.includes('arrayStart') || expr.includes('arrayEnd')) return match
    const parts = expr.split('|')
    const path = parts[0]
    const fmt = parts[1] ? parts[1].trim() : null
    const value = resolvePath(data, path)
    if (value === undefined || value === null) return ''
    return applyFormat(value, fmt)
  })
}

function renderArrayBlocks(content, data) {
  let result = content
  const startRe = /{{([\w.]+)\|arrayStart}}/g
  let match
  // eslint-disable-next-line no-cond-assign
  while ((match = startRe.exec(result)) !== null) {
    const key = match[1]
    const startMarker = match[0]
    const endMarker = '{{' + key + '|arrayEnd}}'
    const startPos = result.indexOf(startMarker) + startMarker.length
    const endPos = result.indexOf(endMarker)
    if (endPos === -1) continue
    const block = result.substring(startPos, endPos)
    let arr = resolvePath(data, key)
    if (!Array.isArray(arr)) arr = []
    const rendered = arr.map(item => substituteVars(block, item)).join('')
    result = result.slice(0, result.indexOf(startMarker)) + rendered + result.slice(endPos + endMarker.length)
    startRe.lastIndex = 0
  }
  return result
}

function getLineWidth(widthMm) {
  return (widthMm <= 58) ? 32 : 48
}

function renderEscposCommands(content, lineWidth, config) {
  const dotsPerMm = 8
  const cfg = config || {}

  const skipInit = content.includes('[[no-init]]')
  content = content.replace(/\[\[no-init\]\]/g, '')

  let header = skipInit ? '' : '\x1B\x40'

  const alignMap = { left: '\x00', center: '\x01', right: '\x02' }
  header += '\x1B\x61' + (alignMap[cfg.escpos_alignment || 'left'] || '\x00')

  const leftMm = parseInt(cfg.escpos_margin_left_mm || 0)
  if (leftMm > 0) {
    const u = Math.min(Math.round(leftMm * dotsPerMm), 65535)
    header += '\x1D\x4C' + String.fromCharCode(u % 256) + String.fromCharCode(Math.floor(u / 256))
  }

  const rightMm = parseInt(cfg.escpos_margin_right_mm || 0)
  if (rightMm > 0) {
    const ticketMm = parseInt(cfg.escpos_ticket_width_mm || 80)
    let pu = Math.max(1, Math.round((ticketMm - leftMm - rightMm) * dotsPerMm))
    pu = Math.min(pu, 65535)
    header += '\x1D\x57' + String.fromCharCode(pu % 256) + String.fromCharCode(Math.floor(pu / 256))
  }

  const topMm = parseInt(cfg.escpos_margin_top_mm || 0)
  if (topMm > 0) {
    const fl = Math.min(Math.max(1, Math.round(topMm / 3.5)), 255)
    header += '\x1B\x64' + String.fromCharCode(fl)
  }

  content = header + content

  const bottomMm = parseInt(cfg.escpos_margin_bottom_mm || 0)
  if (bottomMm > 0) {
    const fl = Math.min(Math.max(1, Math.round(bottomMm / 3.5)), 255)
    const feed = '\x1B\x64' + String.fromCharCode(fl)
    for (const tag of ['[[cut]]', '[[cut=full]]', '[[cut=partial]]']) {
      content = content.split(tag).join(feed + tag)
    }
  }

  // [[col]] — separador izquierda/derecha por línea
  content = content.split('\n').map(line => {
    if (!line.includes('[[col]]')) return line
    const [left, right = ''] = line.split('[[col]]')
    const l = left.trimEnd()
    const r = right.trimStart()
    const lClean = l.replace(/\[\[[^\]]+\]\]/g, '')
    const rClean = r.replace(/\[\[[^\]]+\]\]/g, '')
    const pad = Math.max(1, lineWidth - lClean.length - rClean.length)
    return l + ' '.repeat(pad) + r
  }).join('\n')

  // [[feed=N]]
  content = content.replace(/\[\[feed=(\d+)\]\]/g, (_, n) =>
    '\x1B\x64' + String.fromCharCode(Math.min(parseInt(n), 255))
  )

  const cmdMap = {
    '[[align=left]]':   '\x1B\x61\x00',
    '[[align=center]]': '\x1B\x61\x01',
    '[[align=right]]':  '\x1B\x61\x02',
    '[[left]]':         '\x1B\x61\x00',
    '[[center]]':       '\x1B\x61\x01',
    '[[right]]':        '\x1B\x61\x02',
    '[[bold]]':         '\x1B\x45\x01',
    '[[bold=1]]':       '\x1B\x45\x01',
    '[[bold:on]]':      '\x1B\x45\x01',
    '[[/bold]]':        '\x1B\x45\x00',
    '[[bold=0]]':       '\x1B\x45\x00',
    '[[bold:off]]':     '\x1B\x45\x00',
    '[[big]]':          '\x1D\x21\x11',
    '[[/big]]':         '\x1D\x21\x00',
    '[[size=2]]':       '\x1D\x21\x11',
    '[[size=dh]]':      '\x1D\x21\x01',
    '[[size=dw]]':      '\x1D\x21\x10',
    '[[size=1]]':       '\x1D\x21\x00',
    '[[/size]]':        '\x1D\x21\x00',
    '[[cut]]':          '\x1D\x56\x00',
    '[[cut=full]]':     '\x1D\x56\x00',
    '[[cut=partial]]':  '\x1D\x56\x01',
  }

  for (const [tag, cmd] of Object.entries(cmdMap)) {
    content = content.split(tag).join(cmd)
  }

  return content
}

// ── API pública ───────────────────────────────────────────────────────────────

async function renderLocalTicket(reference, localData) {
  const cache = await idbGet(reference)
  if (!cache || !cache.template || !cache.template.content) {
    throw new Error('Sin cache de ticket para "' + reference + '". Conectate al menos una vez.')
  }

  const data = { ...localData, business: cache.business || {} }
  const tmpl = cache.template
  const lineWidth = getLineWidth(tmpl.escpos_ticket_width_mm || 80)

  let content = tmpl.content
  content = renderArrayBlocks(content, data)
  content = substituteVars(content, data)
  content = renderEscposCommands(content, lineWidth, tmpl)

  return content
}

export default { warmCache, renderLocalTicket }
